import { Alert, LinearProgress } from "@mui/material";
import type { FC } from "react";
import { AdditionalServicesService } from "../../modules/additional-services/additional-services-service";
import { CrudDataGrid } from "../../modules/generic/components/crud-data-grid/crud-data-grid";
import type { Row } from "../../modules/generic/components/crud-data-grid/types";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import type { ISO8601 } from "../../utils/iso8601";
import { formatDatetime, fromISO8601 } from "../../utils/iso8601";
import type { UUID } from "../../utils/uuid";

type AdditionalServiceRow = {
  id: UUID;
  accountName: string;
  serviceType: string;
  createdAt: ISO8601;
  name?: string;
  email?: string;
};

const AdditionalServices: FC = () => {
  const { data, loading, error } = useSupabaseLoader<{ data: AdditionalServiceRow[] }>(
    async ({ supabase }) => {
      const service = new AdditionalServicesService(supabase);

      const { data: servicesData, error: servicesError } = await service.getServicesInterest();

      if (servicesError || !servicesData)
        throw new Error("Could not load additional services interest");

      const rows = servicesData.map((s) => {
        return {
          id: s.id,
          accountName: s.account.name,
          serviceType: s.service_type,
          createdAt: s.created_at,
          name: s.profile?.name,
          email: s.profile?.email ?? undefined,
        };
      });

      return { data: rows };
    },
    [],
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return (
    <Page title="Adminstration | ChangeEngine">
      <PageHeader display="flex">
        <PageHeading heading="Additional Services" subheading="Interest shown" />
      </PageHeader>
      <PageContent>
        <CrudDataGrid<Row<AdditionalServiceRow>>
          initialRows={data.data}
          columns={{
            accountName: {
              headerName: "Account Name",
              width: 200,
            },
            serviceType: {
              headerName: "Service Type",
              width: 200,
            },
            createdAt: {
              headerName: "Created At",
              width: 200,
              valueFormatter: (_, row) => formatDatetime(fromISO8601(row.createdAt)),
            },
            name: {
              headerName: "Name",
              width: 200,
            },
            email: {
              headerName: "Email",
              width: 200,
            },
          }}
          hideActions={true}
          gridOverrides={{
            initialState: {
              pagination: { paginationModel: { pageSize: 10 } },
            },
            pagination: true,
          }}
        />
      </PageContent>
    </Page>
  );
};

export default AdditionalServices;
